import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { PAYOUT_TYPE_OPTIONS } from 'src/constants/payment/constants'
import { getPayoutSettingsSaving } from 'src/selectors/paymentConfig'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { PayoutSettingsFormValues } from './PayoutSettingsForm.interface'

// One group per pricing route: heading and its two fields 16px apart, groups 32px apart — the
// same rhythm as the Settings page, so the eye reads four routes rather than eight loose fields.
const sectionStyles = tw`flex flex-col gap-4`
const fieldsStyles = tw`grid grid-cols-1 gap-4 md:grid-cols-2`

/**
 * Field order and labels are the live Retool tab's, verbatim: buyer is fee-then-rate while loan
 * and wire are rate-then-fee, and the two fee labels drop "Transfer". The inconsistency is
 * preserved on purpose so nobody moving between the two tabs has to re-find a field.
 */
interface Props {
  // Without `payout_settings_manage`: fields disabled and no Save button.
  isReadOnly: boolean
}

const PayoutSettingsFormContentComponent = ({ isReadOnly }: Props) => {
  const isSaving = useSelector(getPayoutSettingsSaving)
  const { dirty } = useFormikContext<PayoutSettingsFormValues>()

  return (
    <SectionCard
      title="Payout Settings"
      subtitle="Controls how and when this customer's contractors get paid."
    >
      <div css={tw`flex flex-col gap-8`}>
        <div css={sectionStyles}>
          <Typography variant="h5" css={tw`text-shades-1000`}>
            Payout
          </Typography>
          <div css={fieldsStyles}>
            <FormikSelect<PayoutSettingsFormValues>
              name="payoutType"
              label="Payout Type"
              options={PAYOUT_TYPE_OPTIONS}
              disabled={isReadOnly}
            />
            <FormikTextField<PayoutSettingsFormValues>
              name="payoutTimeInHours"
              placeholder="0"
              label="Payout Time in Hours"
              disabled={isReadOnly}
            />
          </div>
        </div>

        <div css={sectionStyles}>
          <Typography variant="h5" css={tw`text-shades-1000`}>
            Buyer
          </Typography>
          <div css={fieldsStyles}>
            <FormikTextField<PayoutSettingsFormValues>
              name="buyerFee"
              placeholder="0"
              label="Buyer Fee (cents)"
              disabled={isReadOnly}
            />
            <FormikTextField<PayoutSettingsFormValues>
              name="buyerRate"
              placeholder="0.00"
              label="Buyer Rate (%)"
              disabled={isReadOnly}
            />
          </div>
        </div>

        <div css={sectionStyles}>
          <Typography variant="h5" css={tw`text-shades-1000`}>
            Loan transfer
          </Typography>
          <div css={fieldsStyles}>
            <FormikTextField<PayoutSettingsFormValues>
              name="loanTransferRate"
              placeholder="0.00"
              label="Loan Transfer Rate (%)"
              disabled={isReadOnly}
            />
            <FormikTextField<PayoutSettingsFormValues>
              name="loanTransferFee"
              placeholder="0"
              label="Loan Fee (cents)"
              disabled={isReadOnly}
            />
          </div>
        </div>

        <div css={sectionStyles}>
          <Typography variant="h5" css={tw`text-shades-1000`}>
            Wire transfer
          </Typography>
          <div css={fieldsStyles}>
            <FormikTextField<PayoutSettingsFormValues>
              name="wireTransferRate"
              placeholder="0.00"
              label="Wire Transfer Rate (%)"
              disabled={isReadOnly}
            />
            <FormikTextField<PayoutSettingsFormValues>
              name="wireTransferFee"
              placeholder="0"
              label="Wire Fee (cents)"
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>
      {isReadOnly ? null : (
        <div css={tw`flex justify-end`}>
          <Button
            type="submit"
            size="small"
            color="dark"
            className={tw`px-4`}
            loading={isSaving}
            disabled={!dirty}
            data-testid="save-payout-settings"
          >
            Save payout settings
          </Button>
        </div>
      )}
    </SectionCard>
  )
}

export const PayoutSettingsFormContent = memo(PayoutSettingsFormContentComponent)
